import { useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, Lock, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ChangePassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const changeToken = location.state?.change_token;
  const studentId = location.state?.student_id;
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!changeToken) {
    return <Navigate to="/student-login" replace />;
  }

  const rules = {
    length: password.length >= 8,
    letter: /[A-Za-z]/.test(password),
    number: /\d/.test(password),
    notDefault: password !== 'E@gles26!' && password.length > 0,
  };
  const allValid = rules.length && rules.letter && rules.number && rules.notDefault;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (!allValid) {
      setError('Please meet all the password requirements.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/student/change-password`,
        { change_token: changeToken, new_password: password },
        { withCredentials: true },
      );
      window.location.href = '/';
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to set new password.');
    } finally {
      setLoading(false);
    }
  };

  const Rule = ({ ok, label }) => (
    <li className={`flex items-center gap-2 text-sm ${ok ? 'text-green-700' : 'text-slate-500'}`}>
      <CheckCircle2 className={`w-4 h-4 ${ok ? 'text-green-600' : 'text-slate-300'}`} />
      {label}
    </li>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-green-50 px-4">
      <Card className="w-full max-w-md p-8 shadow-xl border border-slate-200">
        <div className="flex flex-col items-center text-center mb-6">
          <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mb-3">
            <Lock className="w-8 h-8 text-blue-700" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Set a New Password</h1>
          <p className="text-slate-500 text-sm mt-1">
            Welcome{studentId ? `, ${studentId}` : ''}! Choose a password you&apos;ll remember.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="pw" className="text-sm text-slate-700">New password</Label>
            <div className="relative mt-1">
              <Input
                id="pw"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pr-9 h-11"
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="pw2" className="text-sm text-slate-700">Confirm password</Label>
            <Input
              id="pw2"
              type={showPassword ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-1 h-11"
              autoComplete="new-password"
              required
            />
          </div>

          <ul className="space-y-1.5 py-2">
            <Rule ok={rules.length} label="At least 8 characters" />
            <Rule ok={rules.letter} label="Contains a letter" />
            <Rule ok={rules.number} label="Contains a number" />
            <Rule ok={rules.notDefault} label="Different from the default password" />
          </ul>

          <Button
            type="submit"
            disabled={loading || !allValid || password !== confirm}
            className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save & Continue'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
